import { useEffect, useState } from 'react';
import { useApi } from '../../services/api';

export default function ItemManagement() {
    const api = useApi();
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<'all' | 'global' | 'custom'>('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState<any>(null);

    useEffect(() => {
        loadItems();
    }, [filter]);

    async function loadItems() {
        try {
            setLoading(true);
            const result = await api.getItems();
            let filtered = result.items || [];

            if (filter === 'global') {
                filtered = filtered.filter((item: any) => !item.tenantId);
            } else if (filter === 'custom') {
                filtered = filtered.filter((item: any) => item.tenantId);
            }

            setItems(filtered);
        } catch (error) {
            console.error('Failed to load items:', error);
        } finally {
            setLoading(false);
        }
    }

    if (loading) {
        return <div className="text-center py-12 text-text-secondary">Loading items...</div>;
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-text-primary">Bingo Item Catalog</h1>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-primary text-white px-4 py-2 rounded hover:opacity-90"
                >
                    + Create Custom Item
                </button>
            </div>

            {/* Filters */}
            <div className="flex gap-2 mb-6">
                <button
                    onClick={() => setFilter('all')}
                    className={`px-4 py-2 rounded ${filter === 'all'
                        ? 'bg-primary text-white'
                        : 'bg-surface text-text-secondary border border-border hover:bg-background'
                        }`}
                >
                    All Items ({items.length})
                </button>
                <button
                    onClick={() => setFilter('global')}
                    className={`px-4 py-2 rounded ${filter === 'global'
                        ? 'bg-primary text-white'
                        : 'bg-surface text-text-secondary border border-border hover:bg-background'
                        }`}
                >
                    Global
                </button>
                <button
                    onClick={() => setFilter('custom')}
                    className={`px-4 py-2 rounded ${filter === 'custom'
                        ? 'bg-primary text-white'
                        : 'bg-surface text-text-secondary border border-border hover:bg-background'
                        }`}
                >
                    Custom
                </button>
            </div>

            {/* Items Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((item) => (
                    <div
                        key={item.id}
                        className="bg-surface rounded-lg shadow p-4 hover:shadow-md transition border border-border"
                    >
                        <div className="flex justify-between items-start mb-2">
                            <h3 className="font-semibold text-sm text-text-primary">{item.title}</h3>
                            {item.isAiRelated && (
                                <span className="bg-purple-500/20 text-purple-400 text-xs px-2 py-1 rounded border border-purple-500/30">
                                    AI
                                </span>
                            )}
                        </div>
                        <p className="text-text-muted text-xs mb-2 line-clamp-2">
                            {item.description}
                        </p>
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-text-secondary">{item.category}</span>
                            <span className={`px-2 py-1 rounded ${item.difficulty === 'easy' ? 'bg-green-500/20 text-green-700 dark:text-green-400 border border-green-500/30' :
                                item.difficulty === 'medium' ? 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border border-yellow-500/30' :
                                    'bg-red-500/20 text-red-700 dark:text-red-400 border border-red-500/30'
                                }`}>
                                {item.difficulty}
                            </span>
                        </div>
                        {item.tenantId && (
                            <div className="mt-2 pt-2 border-t border-border flex gap-2">
                                <button
                                    onClick={() => {
                                        setSelectedItem(item);
                                        setShowEditModal(true);
                                    }}
                                    className="flex-1 text-xs bg-background text-text-secondary px-2 py-1 rounded hover:bg-border border border-border"
                                >
                                    Edit
                                </button>
                                <button className="flex-1 text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded hover:bg-red-500/30 border border-red-500/30">
                                    Delete
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {items.length === 0 && (
                <div className="bg-surface rounded-lg shadow p-12 text-center border border-border">
                    <p className="text-text-secondary text-lg">No items found</p>
                </div>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <CreateItemModal
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={() => {
                        setShowCreateModal(false);
                        loadItems();
                    }}
                />
            )}

            {/* Edit Modal */}
            {showEditModal && selectedItem && (
                <EditItemModal
                    item={selectedItem}
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedItem(null);
                    }}
                    onSuccess={() => {
                        setShowEditModal(false);
                        setSelectedItem(null);
                        loadItems();
                    }}
                />
            )}
        </div>
    );
}

function CreateItemModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
    const api = useApi();
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: '',
        difficulty: 'medium',
        isAiRelated: false,
    });

    async function handleSubmit() {
        if (!formData.title.trim() || !formData.description.trim()) {
            alert('Title and description are required');
            return;
        }

        try {
            await api.createItem(formData);
            onSuccess();
        } catch (error) {
            console.error('Failed to create item:', error);
            alert('Failed to create item');
        }
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-surface rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto border border-border">
                <h2 className="text-2xl font-bold mb-4 text-text-primary">Create Custom Item</h2>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            Title *
                        </label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                            placeholder="e.g., Deployed to production"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            Description *
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                            rows={3}
                            placeholder="Detailed description..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            Category
                        </label>
                        <input
                            type="text"
                            value={formData.category}
                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                            placeholder="e.g., Delivery and Reliability"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            Difficulty
                        </label>
                        <select
                            value={formData.difficulty}
                            onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                        >
                            <option value="easy">Easy</option>
                            <option value="medium">Medium</option>
                            <option value="hard">Hard</option>
                        </select>
                    </div>

                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="isAiRelated"
                            checked={formData.isAiRelated}
                            onChange={(e) => setFormData({ ...formData, isAiRelated: e.target.checked })}
                            className="mr-2"
                        />
                        <label htmlFor="isAiRelated" className="text-sm text-text-secondary">
                            AI-related item
                        </label>
                    </div>
                </div>

                <div className="flex gap-3 mt-6">
                    <button
                        onClick={onClose}
                        className="flex-1 bg-background text-text-secondary px-4 py-2 rounded hover:bg-border border border-border"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="flex-1 bg-primary text-white px-4 py-2 rounded hover:opacity-90"
                    >
                        Create
                    </button>
                </div>
            </div>
        </div>
    );
}

function EditItemModal({ item, onClose, onSuccess }: { item: any; onClose: () => void; onSuccess: () => void }) {
    const api = useApi();
    const [formData, setFormData] = useState({
        title: item.title,
        description: item.description,
        category: item.category,
        difficulty: item.difficulty,
        isAiRelated: item.isAiRelated,
    });

    async function handleSubmit() {
        if (!formData.title.trim() || !formData.description.trim()) {
            alert('Title and description are required');
            return;
        }

        try {
            await api.updateItem(item.id, formData);
            onSuccess();
        } catch (error) {
            console.error('Failed to update item:', error);
            alert('Failed to update item');
        }
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-surface rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto border border-border">
                <h2 className="text-2xl font-bold mb-4 text-text-primary">Edit Custom Item</h2>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            Title *
                        </label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                            placeholder="e.g., Deployed to production"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            Description *
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                            rows={3}
                            placeholder="Detailed description..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            Category
                        </label>
                        <input
                            type="text"
                            value={formData.category}
                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                            placeholder="e.g., Delivery and Reliability"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            Difficulty
                        </label>
                        <select
                            value={formData.difficulty}
                            onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary"
                        >
                            <option value="easy">Easy</option>
                            <option value="medium">Medium</option>
                            <option value="hard">Hard</option>
                        </select>
                    </div>

                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="editIsAiRelated"
                            checked={formData.isAiRelated}
                            onChange={(e) => setFormData({ ...formData, isAiRelated: e.target.checked })}
                            className="mr-2"
                        />
                        <label htmlFor="editIsAiRelated" className="text-sm text-text-secondary">
                            AI-related item
                        </label>
                    </div>
                </div>

                <div className="flex gap-3 mt-6">
                    <button
                        onClick={onClose}
                        className="flex-1 bg-background text-text-secondary px-4 py-2 rounded hover:bg-border border border-border"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="flex-1 bg-primary text-white px-4 py-2 rounded hover:opacity-90"
                    >
                        Update
                    </button>
                </div>
            </div>
        </div>
    );
}
